import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Ship } from '../../interfaces/types';
import { ShipGarageList } from '../../components/garage/ShipGarageList';
import { ShipGarageRepair } from '../../components/garage/ShipGarageRepair';
import { SearchBar } from '../../components/ships/SearchBar';
import { Pagination } from '../../components/common/Pagination';
import { useApi } from '../../hooks/useApi';
import { Header } from '../../components/common/Header';
import { Navigation } from '../../components/common/Navigation';
import { ShipComparison } from '../../components/ships/ShipComparison';
import './ShipGarage.css';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import { useUser } from '../../hooks/useUser';

export const ShipGarage: React.FC = () => {
  const [selectedShip, setSelectedShip] = useState<Ship | null>(null);
  const [isCompareMode, setIsCompareMode] = useState(false);
  const [shipsToCompare, setShipsToCompare] = useState<Ship[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const { username } = useUser();
  const { data: ships, loading, error, refetch } = useApi<Ship[]>('/flota');
  
  // Estado y lógica del menú hamburguesa
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cerrar menú al cambiar de ruta
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  // Filtrar solo las naves del usuario
  const userShips = useMemo(() => {
    return ships?.filter(ship => ship.usuario === username) || [];
  }, [ships, username]);

  // Filtrar naves por búsqueda
  const filteredShips = useMemo(() => {
    if (!searchQuery.trim()) return userShips;
    
    const query = searchQuery.toLowerCase();
    return userShips.filter(ship => 
      ship.name.toLowerCase().includes(query) ||
      (ship.nick && ship.nick.toLowerCase().includes(query)) ||
      ship.manufacturer.name.toLowerCase().includes(query) ||
      ship.production_status.toLowerCase().includes(query)
    );
  }, [userShips, searchQuery]);

  // Paginación
  const totalPages = Math.ceil(filteredShips.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedShips = filteredShips.slice(startIndex, startIndex + itemsPerPage);

  // Resetear página cuando cambia la búsqueda
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleShipSelect = (ship: Ship) => {
    setSelectedShip(ship);
  };

  const handleCompareToggle = () => {
    setIsCompareMode(!isCompareMode);
    if (isCompareMode) {
      setShipsToCompare([]);
    }
  };

  const handleShipCompareSelect = (ship: Ship) => {
    if (shipsToCompare.find(s => s.name === ship.name)) {
      setShipsToCompare(shipsToCompare.filter(s => s.name !== ship.name));
    } else if (shipsToCompare.length < 4) {
      setShipsToCompare([...shipsToCompare, ship]);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  return (
    <ProtectedRoute>
      <div className="page">
        <Header 
          onToggleMenu={toggleMenu}
          isMenuOpen={isMenuOpen}
        />
        
        <nav className="navigation-section" ref={menuRef}>
          <Navigation 
            onCompareToggle={handleCompareToggle}
            isCompareMode={isCompareMode}
            compareCount={shipsToCompare.length}
            showCompareButton={true}
            isMenuOpen={isMenuOpen}
            onCloseMenu={closeMenu}
          />
        </nav>

        <div className="search-section">
          <SearchBar 
            onSearch={setSearchQuery}
            onClearFilters={handleClearFilters}
            minChars={1}
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>

        <div className="ship-garage">
          {loading ? (
            <div className="loading">Cargando garage...</div>
          ) : error ? (
            <div className="error">Error al cargar el garage: {error.message}</div>
          ) : userShips.length === 0 ? (
            <div className="empty-garage">No tienes naves en tu garage</div>
          ) : selectedShip ? (
            <ShipGarageRepair 
              ship={selectedShip}
              onClose={() => setSelectedShip(null)}
            />
          ) : (
            <>
              {isCompareMode && shipsToCompare.length >= 2 && (
                <div className="comparison-layout">
                  <ShipComparison ships={shipsToCompare} />
                  <div className="compared-ships-list">
                    {shipsToCompare.map(ship => (
                      <div key={ship.name} className="compared-ship-card">
                        <img src={ship.imagen} alt={ship.name} />
                        <h3>{ship.name}</h3>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {filteredShips.length === 0 ? (
                <div className="no-results">
                  No se encontraron naves que coincidan con "{searchQuery}"
                </div>
              ) : (
                <ShipGarageList 
                  ships={displayedShips}
                  totalShips={filteredShips.length}
                  onShipSelect={handleShipSelect}
                  onRefresh={refetch}
                  isCompareMode={isCompareMode}
                  onCompareSelect={handleShipCompareSelect}
                  selectedForCompare={shipsToCompare.map(s => s.name)}
                />
              )}
            </>
          )}
        </div>
      </div>
    </ProtectedRoute>
  );
}; 